import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const emptyForm = { name: '', email: '', password: '', role: 'outlet_owner', outlet: '' };

// Roles that can be created manually from this page
const ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'outlet_owner', label: 'Outlet Manager' },
];

// All user types in the system (for the reference cards)
const ALL_ROLE_TYPES = [
  { value: 'super_admin', label: 'Super Admin', note: 'Full system access' },
  { value: 'organization', label: 'Organization', note: 'Auto-created in the Organizations module' },
  { value: 'outlet_owner', label: 'Outlet Manager', note: 'Auto-created in the Outlets module, or here' },
];

export default function UsersPage() {
  const { hasRole } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get('/outlets').then((r) => setOutlets(r.data)); }, []);

  if (!hasRole('super_admin', 'outlet_owner')) {
    return <div className="page"><div className="empty-state">Access denied.</div></div>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/register', form);
      toast.success('User created');
      setShowModal(false);
      setForm(emptyForm);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error creating user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>User Management</h2>
          <p className="text-muted">Create and manage system users</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> Add User
        </button>
      </div>

      <div className="info-banner">
        Users are created here and given access to the system. Each user is assigned a role that determines what they can see and do.
      </div>

      <div className="roles-grid">
        {ALL_ROLE_TYPES.map((r) => (
          <div key={r.value} className="role-card">
            <h4>{r.label}</h4>
            <p className="text-muted role-value">{r.value}</p>
            <p className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>{r.note}</p>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create User</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              {['outlet_owner'].includes(form.role) && (
                <div className="form-group">
                  <label>Assign Outlet</label>
                  <select value={form.outlet} onChange={(e) => setForm({ ...form, outlet: e.target.value })}>
                    <option value="">All Outlets</option>
                    {outlets.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
                  </select>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Creating...' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
